import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from './api/client';
import {
  SECTION_CATALOG,
  type SectionId,
  type SectionVisibility,
} from './api/generated/section-catalog';

interface SectionContextValue {
  sections: SectionVisibility[];
  loading: boolean;
  error: string;
  visibleSections: (typeof SECTION_CATALOG)[number][];
  refresh(): Promise<void>;
  update(id: SectionId, visible: boolean): Promise<void>;
}

const SectionContext = createContext<SectionContextValue | null>(null);

export function SectionProvider({ children }: { children: ReactNode }) {
  const [sections, setSections] = useState<SectionVisibility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    try {
      const rows = await api.sections();
      // A read started before a successful edit must not undo that edit in this session.
      setSections((current) =>
        rows.map((row) => {
          const previous = current.find((item) => item.id === row.id);
          return previous && previous.revision > row.revision ? previous : row;
        }),
      );
      setError('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Не удалось загрузить разделы');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const onFocus = () => {
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const update = useCallback(
    async (id: SectionId, visible: boolean) => {
      const current = sections.find((section) => section.id === id);
      if (!current) throw new Error('Настройки раздела не загружены');
      const saved = await api.updateSection(id, { visible, revision: current.revision });
      setSections((rows) =>
        rows.map((row) => (row.id === saved.id && row.revision < saved.revision ? saved : row)),
      );
    },
    [sections],
  );

  const value = useMemo(
    () => ({
      sections,
      loading,
      error,
      refresh,
      update,
      visibleSections: SECTION_CATALOG.filter(({ id }) =>
        sections.some((row) => row.id === id && row.visible),
      ),
    }),
    [sections, loading, error, refresh, update],
  );

  return <SectionContext.Provider value={value}>{children}</SectionContext.Provider>;
}

export function useSections(): SectionContextValue {
  const value = useContext(SectionContext);
  if (!value) throw new Error('useSections must be used within SectionProvider');
  return value;
}
